/**
 * @file haulagePoDetails.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-19
 * @modified 2026-10-19
 * @description A PO-H's own details, set by a person (Cam, 19 Oct 2026: "edit
 *              the collection address on a PO-H please and add notes to it for
 *              internal use only and also notes for haulier to see"). Generated
 *              to utils/haulagePoDetails.js by scripts/sync-shared-utils.js —
 *              edit this copy.
 *
 *   A PO-H's lines are rebuilt from its order (or self-billed PO) whenever that
 *   changes, so a collection address typed into a line would be lost on the
 *   next save. The override is kept apart, per COLLECTION POINT — the supplier
 *   (or our own stock) a line is collected from — and laid over the lines when
 *   they are read: the PDF, the email, the PO window. A rebuild that keeps the
 *   same supplier keeps the override; a line moved to another supplier is
 *   collected from that supplier's own address until someone says otherwise.
 *
 *   Stored on purchasing.purchase_orders (migrations/haulage_po_details.sql):
 *     collection_overrides jsonb  { "<point key>": { from, address } }
 *     haulier_notes text          printed on the PO-H for the haulier
 *     internal_notes text         ours only — never printed, never sent
 *
 *   1.1.0 (2026-10-19, Cam: "allow the edit button to work in PO-H modal and
 *   PO search table … even if only the delivery address and notes can be
 *   edited … can the customer field be a searchable dropdown, and auto fill the
 *   delivery address stored with the selected customer"): the PO-H's own
 *   DELIVERY — who it is delivered to, the address and the instructions —
 *   kept the same way (migrations/haulage_po_delivery_override.sql):
 *     delivery_override jsonb  { to?: { account_id, name, address },
 *                                instructions?: text }
 *   `to` absent = deliver where the order (or self-billed PO) says;
 *   `instructions` absent = the order's. The PO-H's own delivery_address /
 *   delivery_instructions columns always hold the delivery IN USE (the
 *   override over the order's), written by the edit and by every rebuild, so
 *   whatever reads those columns — the PDF, the email, Search POs — reads it
 *   without knowing an override exists. The order itself is never changed;
 *   its customer (customer_id / customer_name, rep scoping) stays the order's.
 */

export const NOTE_MAX = 2000;
export const ADDRESS_MAX = 600;
export const NAME_MAX = 120;

const clean = (v) => String(v ?? '').replace(/\r\n?/g, '\n').trim();

/** The collection point a PO-H line belongs to: our stock, else its supplier by name. */
export function collectionKeyOf(line) {
  if (line && line.fromStock) return 'stock';
  const name = clean(line && line.collectFrom).toLowerCase().replace(/\s+/g, ' ');
  return name ? `s:${name}` : 'none';
}

/** The stored overrides, read safely (anything malformed is no override). */
export function readOverrides(raw) {
  let o = raw;
  if (typeof o === 'string') {
    try { o = JSON.parse(o); } catch (e) { o = null; }
  }
  if (!o || typeof o !== 'object' || Array.isArray(o)) return {};
  const out = {};
  for (const [k, v] of Object.entries(o)) {
    if (!v || typeof v !== 'object') continue;
    const from = clean(v.from);
    const address = clean(v.address);
    if (from || address) out[k] = { from, address };
  }
  return out;
}

/**
 * The PO-H's collection points, each with what the order says and what is
 * used (the override where one is set).
 * @returns {Array<{ key, from, address, computedFrom, computedAddress, overridden, lines }>}
 */
export function collectionPoints(lines, overridesRaw) {
  const overrides = readOverrides(overridesRaw);
  const points = [];
  const byKey = new Map();
  (Array.isArray(lines) ? lines : []).forEach((l) => {
    const key = collectionKeyOf(l);
    if (!byKey.has(key)) {
      const p = {
        key,
        computedFrom: clean(l.collectFrom) || (l.fromStock ? 'Our stock' : ''),
        computedAddress: clean(l.collectFromAddress),
        lines: 0
      };
      byKey.set(key, p);
      points.push(p);
    }
    byKey.get(key).lines += 1;
  });
  return points.map((p) => {
    const o = overrides[p.key] || null;
    return {
      ...p,
      from: (o && o.from) || p.computedFrom,
      address: (o && o.address) || p.computedAddress,
      overridden: !!o
    };
  });
}

/** The lines with each collection point's override laid over them (collectOverridden marks them). */
export function applyCollection(lines, overridesRaw) {
  const overrides = readOverrides(overridesRaw);
  return (Array.isArray(lines) ? lines : []).map((l) => {
    const o = overrides[collectionKeyOf(l)];
    if (!o) return l;
    return {
      ...l,
      collectFrom: o.from || l.collectFrom,
      collectFromAddress: o.address || l.collectFromAddress,
      collectOverridden: true
    };
  });
}

/**
 * What a save asks for, checked: one entry per collection point the PO-H has
 * (an override equal to what the order says is no override), and the notes.
 * @param {{ collection?: Array<{ key, from, address }>, haulierNotes?, internalNotes? }} body
 * @param {Array} lines  the PO-H's lines as stored
 * @returns {{ ok: true, overrides, haulierNotes, internalNotes } | { ok: false, error }}
 */
export function cleanDetails(body, lines) {
  const b = body || {};
  const points = collectionPoints(lines, null);
  const known = new Map(points.map((p) => [p.key, p]));
  const overrides = {};
  for (const c of (Array.isArray(b.collection) ? b.collection : [])) {
    const p = known.get(String(c && c.key));
    if (!p) return { ok: false, error: 'A collection point is no longer on this PO-H — reload it' };
    const from = clean(c.from);
    const address = clean(c.address);
    if (from.length > NAME_MAX) return { ok: false, error: `A collection name is at most ${NAME_MAX} characters` };
    if (address.length > ADDRESS_MAX) return { ok: false, error: `A collection address is at most ${ADDRESS_MAX} characters` };
    const o = {
      from: from && from !== p.computedFrom ? from : '',
      address: address && address !== p.computedAddress ? address : ''
    };
    if (o.from || o.address) overrides[p.key] = o;
  }
  const haulierNotes = clean(b.haulierNotes);
  const internalNotes = clean(b.internalNotes);
  if (haulierNotes.length > NOTE_MAX || internalNotes.length > NOTE_MAX) {
    return { ok: false, error: `Notes are at most ${NOTE_MAX} characters` };
  }
  return { ok: true, overrides, haulierNotes, internalNotes };
}

/** The collection as one line of text per point, for the history ("Mill: address"). */
export function collectionText(lines, overridesRaw) {
  return collectionPoints(lines, overridesRaw)
    .filter((p) => p.overridden)
    .map((p) => `${p.from}${p.address ? `: ${p.address.replace(/\n+/g, ', ')}` : ''}`)
    .join(' | ');
}

/* ---- Delivery (1.1.0) ------------------------------------------------- */

const positiveId = (v) => (Number.isInteger(Number(v)) && Number(v) > 0 ? Number(v) : null);

/**
 * The stored delivery override, read safely: { to: { accountId, name, address }
 * | null, instructions: string | null } — null for each means "the order's" —
 * or null when nothing is overridden (or the value is malformed).
 */
export function readDeliveryOverride(raw) {
  let o = raw;
  if (typeof o === 'string') {
    try { o = JSON.parse(o); } catch (e) { o = null; }
  }
  if (!o || typeof o !== 'object' || Array.isArray(o)) return null;
  const t = o.to && typeof o.to === 'object' && !Array.isArray(o.to) ? o.to : null;
  const to = t && clean(t.address)
    ? { accountId: positiveId(t.account_id ?? t.accountId), name: clean(t.name), address: clean(t.address) }
    : null;
  const instructions = typeof o.instructions === 'string' ? clean(o.instructions) : null;
  if (!to && instructions === null) return null;
  return { to, instructions };
}

/** The override as it is stored (snake_case), or null for none. */
export function storedDeliveryOverride(o) {
  if (!o) return null;
  const out = {};
  if (o.to) out.to = { account_id: o.to.accountId ?? null, name: o.to.name || '', address: o.to.address };
  if (o.instructions !== null && o.instructions !== undefined) out.instructions = o.instructions;
  return Object.keys(out).length ? out : null;
}

/**
 * The delivery IN USE: the override laid over what the order (or self-billed
 * PO) says. `computed` = { accountId, name, address, instructions }.
 */
export function effectiveDelivery(computed, raw) {
  const c = computed || {};
  const o = readDeliveryOverride(raw);
  return {
    accountId: o && o.to ? o.to.accountId : positiveId(c.accountId),
    name: o && o.to ? (o.to.name || clean(c.name)) : clean(c.name),
    address: o && o.to ? o.to.address : clean(c.address),
    instructions: o && o.instructions !== null ? o.instructions : clean(c.instructions),
    toOverridden: !!(o && o.to),
    instructionsOverridden: !!(o && o.instructions !== null)
  };
}

/**
 * What a save asks for, checked against what the order says. A delivery equal
 * to the order's is no override ("Back to the order's address" is simply the
 * order's values sent back).
 * @param {{ accountId?, address?, instructions? }} body
 * @param {{ accountId, name, address, instructions }} computed  the order's
 * @param {{ id, name }|null} contact  the chosen contact as the database has
 *        it (its name is the database's, never the request's); null = the
 *        order's own (or none chosen)
 * @returns {{ ok: true, override } | { ok: false, error }}
 */
export function cleanDelivery(body, computed, contact) {
  const b = body || {};
  const c = computed || {};
  const address = clean(b.address);
  const instructions = clean(b.instructions);
  if (address.length > ADDRESS_MAX) return { ok: false, error: `The delivery address is at most ${ADDRESS_MAX} characters` };
  if (instructions.length > NOTE_MAX) return { ok: false, error: `Delivery instructions are at most ${NOTE_MAX} characters` };
  const chosenId = contact ? positiveId(contact.id) : positiveId(c.accountId);
  const sameTo = chosenId === positiveId(c.accountId) && address === clean(c.address);
  let to = null;
  if (!sameTo) {
    if (!address) return { ok: false, error: 'Give the delivery address — or go back to the order\'s' };
    to = { accountId: chosenId, name: contact ? clean(contact.name) : clean(c.name), address };
  }
  const instr = instructions === clean(c.instructions) ? null : instructions;
  return { ok: true, override: to || instr !== null ? { to, instructions: instr } : null };
}

/** The delivery as one line of text, for the history ("Name: line, line"). */
export function deliveryText(eff) {
  if (!eff) return '';
  const addr = clean(eff.address).replace(/\n+/g, ', ');
  return [clean(eff.name), addr].filter(Boolean).join(': ');
}
